import type { GpsStatus } from 'cb-model';

export type GpsPosition = {
  latitude: number;
  longitude: number;
  gps_status: GpsStatus;
};

export const getGpsPosition = async (timeoutMs: number = 1500): Promise<GpsPosition> => {
  if (typeof navigator === 'undefined') {
    return { latitude: 0, longitude: 0, gps_status: 'unavailable' };
  }

  if (!('geolocation' in navigator)) {
    return { latitude: 0, longitude: 0, gps_status: 'unavailable' };
  }

  // If the browser supports the Permissions API, use it to avoid wasting time when permission is denied,
  // and to ensure we actually trigger the permission prompt when state is 'prompt'.
  let permissionState: PermissionState | 'unknown' = 'unknown';
  try {
    if ('permissions' in navigator && navigator.permissions?.query) {
      const status = await navigator.permissions.query({
        name: 'geolocation' as PermissionName,
      });
      permissionState = status.state;
    }
  } catch {
    // ignore and fall back to calling geolocation directly
  }

  if (permissionState === 'denied') {
    return { latitude: 0, longitude: 0, gps_status: 'permission_denied' };
  }

  return await new Promise<GpsPosition>((resolve) => {
    let settled = false;
    const finish = (value: GpsPosition) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };

    const timer = setTimeout(() => finish({ latitude: 0, longitude: 0, gps_status: 'timed_out' }), timeoutMs);

    // If permission is still prompting, don't apply a super-short geolocation timeout; we want the
    // browser prompt to remain usable, even if our caller only waits a short time.
    const geolocationTimeoutMs = permissionState === 'prompt' ? Math.max(timeoutMs, 30000) : timeoutMs;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latitude = pos.coords.latitude;
        const longitude = pos.coords.longitude;
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          finish({ latitude: 0, longitude: 0, gps_status: 'unavailable' });
          return;
        }
        finish({ latitude, longitude, gps_status: 'captured' });
      },
      () => finish({ latitude: 0, longitude: 0, gps_status: 'error' }),
      {
        enableHighAccuracy: true,
        timeout: geolocationTimeoutMs,
        maximumAge: 0,
      },
    );
  });
};
